import React, { FC } from 'react';
import { Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import type Specialist from '../../../model/Specialist';
import { parseAppointmentLengthMinutes } from '../../../utils/expand-timeslots';
import { getEarliestLabel } from '../../hooks/use-specialist-availabilities';
import type { SpecialistAvailability } from '../../hooks/use-specialist-availabilities';
import NextAvailableBadge from '../next-available-badge';

interface ClinicianOptionsProps {
  specialists: Specialist[] | undefined
  selectedId: string
  getAvailability: (specialistId: string) => SpecialistAvailability
  /** Mobile: compact rows; desktop: cards with title, specialities and badge. */
  mobileStack: boolean
  onSelect: (specialistId: string) => void
}

interface OptionProps {
  specialist: Specialist
  selected: boolean
  isDisabled: boolean
  earliestLabel: string | null
  onSelect: (specialistId: string) => void
}

interface DesktopOptionProps extends OptionProps {
  isLoading: boolean
}

const getMobileClinicianButtonClass = (isDisabled: boolean, selected: boolean): string => {
  if (isDisabled) return 'border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed';
  if (selected) return 'border-brand bg-brand bg-opacity-5';
  return 'border-slate-200 bg-white cursor-pointer';
};

const MobileOption: FC<OptionProps> = ({
  specialist, selected, isDisabled, earliestLabel, onSelect,
}) => (
  <button
    type="button"
    disabled={isDisabled}
    onClick={() => onSelect(specialist.id)}
    className={`min-h-clinician-card flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${getMobileClinicianButtonClass(isDisabled, selected)}`}
  >
    <Avatar
      src={specialist.avatarLink}
      icon={<UserOutlined />}
      size={32}
      className="border border-brand flex-shrink-0"
    />
    <span className="flex-1 min-w-0">
      <b className="block text-sm font-semibold text-slate-800">
        {specialist.firstName}
        {' '}
        {specialist.lastName}
      </b>
      {earliestLabel && (
        <small className="block text-xs text-slate-500 mt-0.5">{earliestLabel}</small>
      )}
    </span>
    <span
      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
        selected && !isDisabled ? 'border-brand bg-brand' : 'border-slate-300 bg-white'
      }`}
    >
      {selected && !isDisabled && <span className="w-2 h-2 rounded-full bg-white block" />}
    </span>
  </button>
);

const DesktopOption: FC<DesktopOptionProps> = ({
  specialist, selected, isDisabled, isLoading, earliestLabel, onSelect,
}) => (
  <button
    type="button"
    disabled={isDisabled}
    onClick={() => onSelect(specialist.id)}
    className={`flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${
      isDisabled
        ? 'border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed'
        : ''
    } ${
      !isDisabled && selected
        ? 'border-brand bg-brand bg-opacity-5'
        : ''
    } ${
      !isDisabled && !selected
        ? 'border-slate-200 bg-white'
        : ''
    }`}
  >
    <Avatar
      src={specialist.avatarLink}
      icon={<UserOutlined />}
      size={48}
      className="border border-brand flex-shrink-0"
    />
    <div className="flex-1 flex items-start justify-between gap-2">
      <div>
        <span className="font-bold text-slate-800 text-sm">
          {specialist.firstName}
          {' '}
          {specialist.lastName}
        </span>
        {specialist.title && <div className="text-xs text-slate-500 mt-0.5">{specialist.title}</div>}
        {specialist.specialities && specialist.specialities.length > 0 && (
          <div className="flex gap-2 mt-1.5 flex-wrap">
            {specialist.specialities.map((s) => <span key={s} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{s}</span>)}
          </div>
        )}
      </div>
      <NextAvailableBadge
        isLoading={isLoading}
        nextAvailableLabel={earliestLabel}
      />
    </div>
    <span
      className={`w-5 h-5 rounded-full border-2 mt-1 flex items-center justify-center flex-shrink-0 ${
        selected && !isDisabled ? 'border-brand bg-brand' : 'border-slate-300 bg-white'
      }`}
    >
      {selected && !isDisabled && <span className="w-2 h-2 rounded-full bg-white block" />}
    </span>
  </button>
);

/** A clinician with no future slot, or still loading, cannot be chosen. */
const ClinicianOptions: FC<ClinicianOptionsProps> = ({
  specialists,
  selectedId,
  getAvailability,
  mobileStack,
  onSelect,
}) => (
  <div className={mobileStack ? 'flex flex-col gap-2' : 'flex flex-col gap-3'}>
    {specialists?.map((specialist) => {
      const availability = getAvailability(specialist.id);
      const earliestLabel = getEarliestLabel(
        availability,
        parseAppointmentLengthMinutes(specialist.appointmentLength),
      );
      const selected = selectedId === specialist.id;
      const isDisabled = (!availability.isLoading && !earliestLabel) || availability.isLoading;
      return mobileStack ? (
        <MobileOption
          key={specialist.id}
          specialist={specialist}
          selected={selected}
          isDisabled={isDisabled}
          earliestLabel={earliestLabel}
          onSelect={onSelect}
        />
      ) : (
        <DesktopOption
          key={specialist.id}
          specialist={specialist}
          selected={selected}
          isDisabled={isDisabled}
          isLoading={availability.isLoading}
          earliestLabel={earliestLabel}
          onSelect={onSelect}
        />
      );
    })}
  </div>
);

export default ClinicianOptions;
